import { useMemo, useRef } from 'react';
import { IFieldReview } from '../review/form/FieldList';
import { IExtractionResults } from '../../types/my-types';
import { transformResultsToFields } from '../../utils/transformResultsToFields';

interface IReviewFieldsSnapshot {
  results: IExtractionResults | undefined;
  fields: IFieldReview[];
}

/**
 * The review field list for an extraction's corrected results.
 *
 * A field keeps its object identity while its result is unchanged, so updating one
 * field's result (a correction) hands every other row the same field as before.
 */
export function useReviewFields(results: IExtractionResults | undefined): IFieldReview[] {
  const previous = useRef<IReviewFieldsSnapshot>({ results: undefined, fields: [] });

  return useMemo(() => {
    const { results: previousResults, fields: previousFields } = previous.current;
    const previousByLabel = new Map(previousFields.map((field) => [field.label, field]));
    const fields = transformResultsToFields(results || {}).map((field) => {
      const unchanged = previousResults?.[field.label] === results?.[field.label];
      return unchanged ? (previousByLabel.get(field.label) ?? field) : field;
    });
    previous.current = { results, fields };
    return fields;
  }, [results]);
}
